import service, { IAsset, IFile, IUploadOpts } from '@/services/asset';
import { IAssetManagerItem, IAssetManagerState } from './types';

const imageFileTypes = [
  'image/jpeg',
  'image/png',
  'image/svg+xml',
];

export default () => ({
  namespaced: true,
  state: () => ({
    assets: [],
  } as IAssetManagerState),
  getters: {
    list: (state: IAssetManagerState) => {
      // return list of assets that have been loaded
      return state
        .assets
        .filter(asset => !!asset.data.src);
    },
  },
  mutations: {
    LOAD_ASSETS(state: IAssetManagerState, assets: IAsset[]) {
      state.assets = assets.map(asset => ({
        data: asset,
      }));
    },
    REMOVE_COMPLETE(state: IAssetManagerState, assetId: string) {
      state.assets = state.assets.filter(asset => (
        !(asset.data && asset.data.id === assetId)
      ));
    },
    REMOVE_ERROR(state: IAssetManagerState, payload: any) {
      // remove removing state from asset
      state.assets = state.assets.map(asset => {
        if (asset.data && asset.data.id === payload.assetId) {
          asset.removing = false;
        }
        return asset;
      });
    },
    REMOVE_START(state: IAssetManagerState, assetId: string) {
      // add removing state to asset
      state.assets = state.assets.map(asset => {
        if (asset.data && asset.data.id === assetId) {
          asset.removing = true;
        }
        return asset;
      });
    },
    UPDATE(state: IAssetManagerState, payload: IAsset[]) {
      state.assets = state.assets.map((asset, index) => ({
        ...asset,
        data: payload[index],
      }));
    },
    UPLOAD_COMPLETE(state: IAssetManagerState, payload: IAssetManagerItem) {
      // update asset data
      state.assets = state.assets.map(asset => {
        if (asset.uploadId === payload.uploadId) {
          asset.data = payload.data;
          asset.dataUrl = payload.dataUrl;
          delete asset.uploadId;
        }
        return asset;
      });
    },
    UPLOAD_ERROR(state: IAssetManagerState, payload: any) {
      // remove failed upload from list of assets
      if (payload.uploadId) {
        state.assets = state.assets.filter(asset => (
          asset.uploadId !== payload.uploadId
        ));
      }
    },
    UPLOAD_START(state: IAssetManagerState, asset: IAssetManagerItem) {
      // add asset with upload id
      state.assets = [
        ...state.assets,
        asset,
      ];
    },
  },
  actions: {
    loadAssets({ commit }: any, assets: IAsset[]) {
      commit('LOAD_ASSETS', assets);
    },
    remove({ commit }: any, assetId: string) {
      commit('REMOVE_START', assetId);
      return service
        .remove({ ids: [assetId] })
        .then(() => commit('REMOVE_COMPLETE', assetId))
        .catch((error) => {
          commit('REMOVE_ERROR', { assetId, error });
          throw error;
        });
    },
    update({ commit }: any, payload: IAsset[]) {
      // update and assume success
      // TODO: fail scenario
      commit('UPDATE', payload);
      return service.update(payload);
    },
    upload(
      { commit }: any,
      { files, options }: { files: IFile[], options: IUploadOpts },
    ) {
      files.forEach(file => {
        const uploadId = Math.random();
        commit('UPLOAD_START', {
          data: options.record,
          uploadId,
        } as IAssetManagerItem);
        service
          .upload(file, options)
          .then(data => {
            const payload: IAssetManagerItem = {
              uploadId,
              dataUrl: '',
              data: {
                ...data,
                ...options.record,
              },
            };

            // add dataUrl for images to enable displaying image in browser
            // rather than waiting for image to be loaded from server
            if (imageFileTypes.includes(file.type)) {
              const reader = new FileReader();
              reader.addEventListener('load', () => {
                payload.dataUrl = reader.result;
                commit('UPLOAD_COMPLETE', payload);
              }, false);

              // load image preview
              reader.readAsDataURL(file);
            } else {
              commit('UPLOAD_COMPLETE', payload);
            }
          })
          .catch((error: any) => {
            commit('UPLOAD_ERROR', { error, uploadId });
            throw error;
          });
      });
    },
    uploadError({ commit }: any, error: any) {
      commit('UPLOAD_ERROR', { error });
    },
  },
});
